import { ExternalLink, Globe, ThumbsUp, ThumbsDown, Star, MapPin, BookOpen } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Website } from "@/data/mockWebsites";
import { Link } from "react-router-dom";
import { PortalListingsCarousel } from "@/components/PortalListingsCarousel";

// Map website names to their review slugs
const REVIEW_SLUGS: Record<string, string> = {
  "ImmobilienScout24.de": "immoscout24-review",
  "Immowelt.de": "immowelt-review",
  "Kleinanzeigen.de – Gewerbeimmobilien": "kleinanzeigen-immobilien-review",
  "JLL.de": "jll-deutschland-review",
  "BNP Paribas Real Estate": "bnp-paribas-real-estate-review",
  "Colliers.de": "colliers-deutschland-review",
  "Savills.de": "savills-deutschland-review",
  "Cushman & Wakefield Germany": "cushman-wakefield-review",
  "CBRE.de": "cbre-deutschland-review",
  "Logivest.de": "logivest-review",
  "MatchOffice.de": "matchoffice-review",
  "Flexas.de": "flexas-review",
  "WeWork.com/de-DE": "wework-deutschland-review",
  "Regus.de": "regus-deutschland-review",
  "GSG Berlin": "gsg-berlin-review",
  "Degewo": "degewo-gewerbe-review",
  "Immobilie1": "immobilie1-review",
  "Immozentrale.eu": "immozentrale-eu",
  "Frank Hoffmann Immobilien": "frank-hoffmann-immobilien",
};

interface WebsiteCardProps {
  website: Website;
  /** Zeigt ein Live-Karussell mit aktuellen Angeboten des Portals über den Vor-/Nachteilen. */
  showLiveListings?: boolean;
  /** Beschränkt die Live-Angebote auf diese Stadt. */
  city?: string;
}

const locationLabel = (location: Website["location"]) => {
  if (!location) return null;
  switch (location.type) {
    case "city": return `${location.name} (Stadt)`;
    case "bundesland": return location.name;
    case "country": return location.name;
  }
};

export const WebsiteCard = ({ website, showLiveListings = false, city }: WebsiteCardProps) => {
  return (
    <Card data-platform-card className="col-span-full min-w-0 w-full overflow-hidden hover:shadow-lg transition-shadow duration-300 bg-card flex flex-col h-full">
      <div className="p-6 flex-1 flex flex-col">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-primary" aria-hidden="true" />
            <h3 className="font-semibold text-lg text-card-foreground">
              {website.name}
            </h3>
          </div>
          <Badge variant="secondary" className="ml-2 shrink-0">
            {website.category}
          </Badge>
        </div>
        
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-1" role="img" aria-label={`Bewertung: ${website.rating} von 5 Sternen`}>
            {Array.from({ length: 5 }, (_, i) => (
              <Star
                key={i}
                className={`w-4 h-4 ${
                  i < website.rating
                    ? "fill-primary text-primary"
                    : "text-muted-foreground"
                }`}
                aria-hidden="true"
              />
            ))}
          </div>
          {website.location && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <MapPin className="w-3 h-3" />
              <span>{locationLabel(website.location)}</span>
            </div>
          )}
        </div>
        
        <p className="text-muted-foreground text-sm mb-4 line-clamp-2 min-h-[2.5rem]">
          {website.description}
        </p>
        
        {showLiveListings && (
          <PortalListingsCarousel platform={website.name} url={website.url} city={city} />
        )}

        <div className="space-y-3 mb-4 flex-1">
          <div className="flex items-start gap-2">
            <ThumbsUp className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-muted-foreground">
              {website.pros}
            </p>
          </div>
          
          <div className="flex items-start gap-2">
            <ThumbsDown className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-muted-foreground">
              {website.cons}
            </p>
          </div>
        </div>
        
        <div className="flex gap-2 mt-auto">
          <Button 
            className="flex-1 group"
            onClick={() => window.open(website.url, '_blank')}
            aria-label={`Website ${website.name} besuchen (öffnet in neuem Tab)`}
          >
            Website besuchen
            <ExternalLink className="w-4 h-4 ml-2 group-hover:translate-x-0.5 transition-transform" aria-hidden="true" />
          </Button>
          {REVIEW_SLUGS[website.name] && (
            <Button variant="outline" className="group" asChild>
              <Link to={`/rezension/${REVIEW_SLUGS[website.name]}`} aria-label={`Review von ${website.name} lesen`}>
                <BookOpen className="w-4 h-4 mr-1" aria-hidden="true" />
                Review
              </Link>
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
};
